/*
   The derived profile file a run used, as the eval-runs API exposes it.

   `scripts/client_metrics derive-profile` writes `data/client/<batch>/detector_profile.json`:
   per setting, the value it chose, the evidence it counted with that value on and off, and the
   decision rule it applied. Ids and counts only, no label content. The run carries the file (or
   its `settings` alone) as `dataset_profile`; this narrows either.
*/

import {
  DETECTOR_SETTING_KEYS,
  type DetectorProfileResolved,
  type DetectorSettingKey,
  type DetectorSource,
} from "@/components/domain/detector-profile"

export interface DatasetProfileEvidence {
  name: string
  value: number | string
}

export interface DatasetProfileSetting {
  key: DetectorSettingKey
  value: string | null
  rule: string | null
  evidence: DatasetProfileEvidence[]
}

export interface DatasetProfile {
  dataset: string | null
  codeRevision: string | null
  derivedAt: string | null
  settings: DatasetProfileSetting[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

const str = (o: Record<string, unknown>, k: string): string | null =>
  typeof o[k] === "string" ? o[k] : null

function evidenceOf(raw: unknown): DatasetProfileEvidence[] {
  if (!isRecord(raw)) return []
  return Object.entries(raw).flatMap(([name, v]) =>
    typeof v === "number" || typeof v === "string" ? [{ name, value: v }] : [],
  )
}

/** Null when there is no file, or it names none of the seven settings. */
export function parseDatasetProfile(raw: unknown): DatasetProfile | null {
  if (!isRecord(raw)) return null
  const settingsRaw = isRecord(raw["settings"]) ? raw["settings"] : raw
  const settings = DETECTOR_SETTING_KEYS.flatMap((key): DatasetProfileSetting[] => {
    const entry = settingsRaw[key]
    if (typeof entry === "string") return [{ key, value: entry, rule: null, evidence: [] }]
    if (!isRecord(entry)) return []
    return [
      {
        key,
        value: str(entry, "value"),
        rule: str(entry, "rule"),
        evidence: evidenceOf(entry["evidence"]),
      },
    ]
  })
  if (settings.length === 0) return null
  return {
    dataset: str(raw, "dataset"),
    codeRevision: str(raw, "code_revision"),
    derivedAt: str(raw, "derived_at"),
    settings,
  }
}

/**
 * One word for where a run's settings came from, for the list's Profile column.
 *
 * A run that used a derived file says so first: that is what makes two runs comparable or not.
 */
export function profileOrigin(
  resolved: DetectorProfileResolved | null,
  dataset: DatasetProfile | null,
): string {
  if (dataset) return "Derived"
  if (!resolved) return "Not recorded"
  // Only the settings with a recorded source: the server fills a setting newer than the run with
  // its built-in value and no source, and that absence is not a second source.
  const sources = new Set<DetectorSource>(
    DETECTOR_SETTING_KEYS.flatMap((k) => {
      const source = resolved.source[k]
      return source === undefined ? [] : [source]
    }),
  )
  if (sources.size > 1) return "Mixed"
  const only = [...sources][0]
  if (only === "default") return "Default"
  if (only === "engagement") return "Engagement"
  if (only === "explicit") return "Explicit"
  return "Recorded"
}
